import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';

import TemplateImageTile from '../../components/catalog/TemplateImageTile';
import { aiApi } from '../../modules/api';
import {
  createCollection,
  getAllCollections,
  subscribeToCollectionChanges,
  updateCollection,
} from '../../modules/collectionStorage';
import { getHistoryTagMap } from '../../modules/historyStorage';
import { HISTORY_ENTRY_REMOVED_EVENT } from '../../constants/history';
import { triggerDownloadFromUrl } from '../../utils/download';
import { fallbackImage } from '../catalog/catalogData';
import '../catalog/catalog.css';

const FALLBACK_USER_ID = 'user-001';
const INITIAL_VISIBLE_GALLERY_ITEMS = 24;
const GALLERY_ITEMS_BATCH_SIZE = 24;
const GALLERY_LAZY_LOAD_DELAY_MS = 450;
const GALLERY_TILE_HEIGHT_RATIOS = [0.75, 1, 1.35, 1.2, 0.67, 1.05];

const getGalleryColumnCount = () => {
  if (typeof window === 'undefined') {
    return 5;
  }

  const width = window.innerWidth;
  if (width <= 520) {
    return 1;
  }
  if (width <= 720) {
    return 2;
  }
  if (width <= 980) {
    return 3;
  }
  if (width <= 1280) {
    return 4;
  }
  return 5;
};

const normalizeSearchValue = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const toTitleCase = (label) =>
  String(label || '')
    .split(/\s+/)
    .map((word) => {
      if (!word) {
        return '';
      }
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ')
    .trim();

const normalizeTagList = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  tags.forEach((tag) => {
    const label =
      typeof tag === 'string'
        ? tag.trim()
        : tag && typeof tag.name === 'string'
        ? tag.name.trim()
        : '';

    if (!label) {
      return;
    }

    const key = label.toLowerCase();
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    normalized.push(toTitleCase(label));
  });

  return normalized;
};

const getPersistedUserId = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return FALLBACK_USER_ID;
  }

  try {
    const rawUser = window.localStorage.getItem('asi:user');
    if (!rawUser) {
      return FALLBACK_USER_ID;
    }

    const parsedUser = JSON.parse(rawUser);
    if (parsedUser && typeof parsedUser.id === 'string' && parsedUser.id.trim()) {
      return parsedUser.id.trim();
    }
  } catch (error) {
    // Fall back to the demo user id when persisted auth cannot be parsed.
  }

  return FALLBACK_USER_ID;
};

const formatCuratorLabel = (name) => {
  if (name && typeof name === 'string' && name.trim()) {
    return `Curated by ${name.trim()}`;
  }
  return 'Workspace collection';
};

const isCharacterSwapTemplate = (item) => {
  if (!item) {
    return false;
  }

  const workflowFields = [
    item.taskType,
    item.type,
    item.mode,
    item.generationMode,
    item.workflow,
  ].map(normalizeSearchValue);
  if (workflowFields.some((value) => value === 'character swap')) {
    return true;
  }

  const title = normalizeSearchValue(item.title);
  const prompt = normalizeSearchValue(item.prompt);
  return (
    item.taskType === 'character_swap'
    || title.includes('character swap')
    || prompt === 'character swap'
    || /^replace the subject in image 1 with\b/i.test(String(item.prompt || ''))
  );
};

const getTemplateKind = (template) =>
  template && isCharacterSwapTemplate(template) ? 'character_swap' : 'image_gen';

const formatGeneratedTime = (isoString) => {
  if (!isoString) {
    return 'Unknown';
  }

  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown';
    }

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (error) {
    return 'Unknown';
  }
};

const formatGalleryArtworkTitle = (item, fallbackTitle) => {
  const source =
    (item && item.title && String(item.title).trim()) ||
    (item && item.description && String(item.description).trim()) ||
    (item && item.prompt && String(item.prompt).trim()) ||
    fallbackTitle;

  const firstLine = String(source || fallbackTitle)
    .replace(/\s+/g, ' ')
    .trim();

  if (firstLine.length <= 64) {
    return firstLine;
  }

  const sentenceBreak = firstLine.slice(0, 64).search(/[.!?;:](?=\s|$)/);
  if (sentenceBreak >= 36) {
    return firstLine.slice(0, sentenceBreak + 1);
  }

  const truncated = firstLine.slice(0, 61).trim();
  return `${truncated.replace(/[,\s]+$/g, '')}...`;
};

const mapHistoryItemToArtwork = (item, index, storedHistoryTags) => {
  if (!item || !item.image || !item.image.url) {
    return null;
  }

  const rawId = item.id || item._id || item.createdAt || `history-${index}`;
  const safeId = `history-${String(rawId)}`;
  const promptText = (item.prompt && String(item.prompt)) || '';
  const descriptionText = (item.description && String(item.description)) || '';
  const isCharacterSwap = isCharacterSwapTemplate(item);
  const itemTags = storedHistoryTags[safeId] || storedHistoryTags[String(rawId)] || item.tags || [];

  return {
    id: safeId,
    rawId: item.id || item._id || null,
    url: item.image.url,
    filename: item.image.filename || 'generated-image.png',
    title: isCharacterSwap
      ? 'Character Swap'
      : formatGalleryArtworkTitle(item, `AI artwork ${index + 1}`),
    prompt: isCharacterSwap ? 'Character Swap' : (descriptionText || promptText),
    originalPrompt: promptText,
    description: descriptionText,
    tags: itemTags,
    taskType: item.taskType || item.type || item.mode || null,
    sourceImages: Array.isArray(item.sourceImages)
      ? item.sourceImages.filter((sourceImage) => sourceImage && sourceImage.url)
      : [],
    createdAt: item.createdAt || null,
  };
};

const mapHistoryArtworkToTemplate = (artwork, index = 0) => {
  if (!artwork || !artwork.url) {
    return null;
  }

  const isCharacterSwap = isCharacterSwapTemplate(artwork);

  return {
    id: `history-template:${artwork.id}`,
    templateId: `history-template:${artwork.id}`,
    source: 'history',
    sourceHistoryId: artwork.rawId || artwork.id,
    title: artwork.title || (isCharacterSwap ? `Character swap ${index + 1}` : `My template ${index + 1}`),
    imageUrl: artwork.url,
    url: artwork.url,
    filename: artwork.filename || 'generated-image.png',
    prompt: artwork.prompt || artwork.title || '',
    originalPrompt: artwork.originalPrompt || '',
    description: artwork.description || '',
    tags: normalizeTagList(artwork.tags),
    visibility: 'private',
    taskType: isCharacterSwap ? 'character_swap' : (artwork.taskType || 'generate'),
    sourceImages: Array.isArray(artwork.sourceImages) ? artwork.sourceImages : [],
    createdAt: artwork.createdAt || null,
    updatedAt: artwork.createdAt || null,
  };
};

const getCollectionVisibility = (collection) =>
  collection && typeof collection.visibility === 'string'
    ? collection.visibility.trim().toLowerCase()
    : 'private';

const buildPublishedTemplateState = (artworks, collections, currentUserId) => {
  const templateIds = new Set();
  const collectionIds = {};

  if (!Array.isArray(artworks) || !Array.isArray(collections)) {
    return { templateIds, collectionIds };
  }

  const artworkLookup = new Map();
  artworks.forEach((artwork) => {
    if (!artwork || !artwork.id) {
      return;
    }

    const templateId = `history-template:${artwork.id}`;
    const keys = [
      artwork.id,
      artwork.rawId,
      artwork.url,
    ].map((value) => (value ? String(value) : '')).filter(Boolean);

    keys.forEach((key) => {
      if (!artworkLookup.has(key)) {
        artworkLookup.set(key, templateId);
      }
    });
  });

  collections.forEach((collection) => {
    if (
      !collection
      || getCollectionVisibility(collection) !== 'public'
      || (currentUserId && collection.ownerId && collection.ownerId !== currentUserId)
      || !Array.isArray(collection.artworks)
    ) {
      return;
    }

    collection.artworks.forEach((artwork) => {
      if (!artwork) {
        return;
      }

      const matchedTemplateId =
        (artwork.id && artworkLookup.get(String(artwork.id)))
        || (artwork.url && artworkLookup.get(String(artwork.url)));

      if (!matchedTemplateId) {
        return;
      }

      templateIds.add(matchedTemplateId);
      collectionIds[matchedTemplateId] = collection.id;
    });
  });

  return { templateIds, collectionIds };
};

const distributeGalleryTemplates = (templates, columnCount) => {
  const safeColumnCount = Math.max(1, columnCount || 1);
  const columns = Array.from({ length: safeColumnCount }, () => ({
    height: 0,
    items: [],
  }));

  templates.forEach((template, index) => {
    const shortestColumn = columns.reduce((selected, column, columnIndex) => {
      if (column.height < columns[selected].height) {
        return columnIndex;
      }
      return selected;
    }, 0);

    columns[shortestColumn].items.push({ template, sourceIndex: index });
    columns[shortestColumn].height += GALLERY_TILE_HEIGHT_RATIOS[index % GALLERY_TILE_HEIGHT_RATIOS.length] + 0.08;
  });

  return columns.map((column) => column.items);
};

const Gallery = ({ history }) => {
  const currentUserId = useMemo(() => getPersistedUserId(), []);
  const [historyArtworks, setHistoryArtworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeGalleryKind, setActiveGalleryKind] = useState('image_gen');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [downloadingTemplateId, setDownloadingTemplateId] = useState(null);
  const [storedCollections, setStoredCollections] = useState(() => getAllCollections());
  const [visibleTemplateLimit, setVisibleTemplateLimit] = useState(INITIAL_VISIBLE_GALLERY_ITEMS);
  const [galleryColumnCount, setGalleryColumnCount] = useState(() => getGalleryColumnCount());
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const loadMoreRef = useRef(null);
  const loadMoreTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return subscribeToCollectionChanges((collections) => {
      setStoredCollections(Array.isArray(collections) ? collections : []);
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setGalleryColumnCount(getGalleryColumnCount());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => () => {
    if (loadMoreTimerRef.current) {
      window.clearTimeout(loadMoreTimerRef.current);
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadHistoryArtworks = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        let offset = 0;
        let hasMore = true;
        const items = [];

        while (hasMore && offset < 500) {
          const { data } = await aiApi.fetchHistory({ limit: 100, offset });
          const responseData = data || {};
          const pageItems = Array.isArray(responseData.items) ? responseData.items : [];
          items.push(...pageItems);

          const pagination = responseData.pagination || {};
          hasMore = Boolean(pagination.hasMore) && pageItems.length > 0;
          offset = Number.isFinite(pagination.nextOffset)
            ? pagination.nextOffset
            : offset + pageItems.length;
        }

        if (isCancelled) {
          return;
        }

        const storedHistoryTags = getHistoryTagMap();
        setHistoryArtworks(
          items
            .map((item, index) => mapHistoryItemToArtwork(item, index, storedHistoryTags))
            .filter(Boolean),
        );
      } catch (error) {
        if (!isCancelled) {
          setLoadError('Unable to load Gallery.');
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Failed to load Gallery history', error);
          }
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadHistoryArtworks();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleHistoryRemoval = (event) => {
      const detail = (event && event.detail) || {};
      const removedIds = [
        detail.id ? String(detail.id) : '',
        detail.safeId ? String(detail.safeId) : '',
      ].filter(Boolean);
      const removedUrls = detail.imageUrl ? [String(detail.imageUrl)] : [];

      if (removedIds.length === 0 && removedUrls.length === 0) {
        return;
      }

      setHistoryArtworks((previous) =>
        previous.filter((artwork) => {
          const artworkId = artwork && artwork.id ? String(artwork.id) : '';
          const normalizedId = artworkId.startsWith('history-')
            ? artworkId
            : `history-${artworkId}`;
          return (
            !removedIds.includes(artworkId)
            && !removedIds.includes(normalizedId)
            && !(artwork && artwork.url && removedUrls.includes(String(artwork.url)))
          );
        }),
      );
    };

    window.addEventListener(HISTORY_ENTRY_REMOVED_EVENT, handleHistoryRemoval);
    return () => {
      window.removeEventListener(HISTORY_ENTRY_REMOVED_EVENT, handleHistoryRemoval);
    };
  }, []);

  const publishedTemplateState = useMemo(
    () => buildPublishedTemplateState(historyArtworks, storedCollections, currentUserId),
    [currentUserId, historyArtworks, storedCollections],
  );

  const galleryTemplates = useMemo(() => {
    return historyArtworks
      .map(mapHistoryArtworkToTemplate)
      .filter(Boolean)
      .map((template) => ({
        ...template,
        visibility: publishedTemplateState.templateIds.has(template.templateId)
          ? 'public'
          : template.visibility,
      }));
  }, [historyArtworks, publishedTemplateState]);

  const galleryCounts = useMemo(() => {
    const counts = {
      image_gen: 0,
      character_swap: 0,
    };

    galleryTemplates.forEach((template) => {
      counts[getTemplateKind(template)] += 1;
    });

    return counts;
  }, [galleryTemplates]);

  const visibleTemplates = useMemo(
    () => galleryTemplates.filter((template) => getTemplateKind(template) === activeGalleryKind),
    [activeGalleryKind, galleryTemplates],
  );

  const renderedTemplates = useMemo(
    () => visibleTemplates.slice(0, visibleTemplateLimit),
    [visibleTemplateLimit, visibleTemplates],
  );

  const hasMoreVisibleTemplates = visibleTemplateLimit < visibleTemplates.length;

  const galleryColumns = useMemo(
    () => distributeGalleryTemplates(renderedTemplates, galleryColumnCount),
    [galleryColumnCount, renderedTemplates],
  );

  useEffect(() => {
    setVisibleTemplateLimit(INITIAL_VISIBLE_GALLERY_ITEMS);
    setIsLoadingMoreTemplates(false);
  }, [activeGalleryKind]);

  const loadMoreGalleryTemplates = useCallback(() => {
    if (!hasMoreVisibleTemplates || isLoadingMoreTemplates || loadMoreTimerRef.current) {
      return;
    }

    setIsLoadingMoreTemplates(true);
    loadMoreTimerRef.current = window.setTimeout(() => {
      setVisibleTemplateLimit((previousLimit) =>
        Math.min(previousLimit + GALLERY_ITEMS_BATCH_SIZE, visibleTemplates.length),
      );
      setIsLoadingMoreTemplates(false);
      loadMoreTimerRef.current = null;
    }, GALLERY_LAZY_LOAD_DELAY_MS);
  }, [hasMoreVisibleTemplates, isLoadingMoreTemplates, visibleTemplates.length]);

  useEffect(() => {
    if (typeof window === 'undefined' || !hasMoreVisibleTemplates) {
      return undefined;
    }

    const handleScroll = () => {
      const scrollBottom = window.scrollY + window.innerHeight;
      const pageBottom = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );

      if (pageBottom - scrollBottom < 700) {
        loadMoreGalleryTemplates();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [hasMoreVisibleTemplates, loadMoreGalleryTemplates]);

  const handleUseTemplate = useCallback(
    (template, referenceMode) => {
      if (!template) {
        return;
      }

      const promptText = (template.prompt || template.title || '').trim();
      if (!promptText) {
        message.warning('That image does not have a reusable prompt yet.');
        return;
      }

      history.push('/ai', {
        prompt: promptText,
        focusTab: 'generate',
        referenceImageUrls: referenceMode && template.imageUrl ? [template.imageUrl] : [],
      });

      if (referenceMode === 'style') {
        message.success('Image loaded as a style reference.');
      } else {
        message.success('Prompt loaded in the generator.');
      }
    },
    [history],
  );

  const handleOpenTemplateDetails = useCallback((template) => {
    setSelectedTemplate(template || null);
  }, []);

  const handleCloseTemplateDetails = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  const handlePublishHistoryTemplate = useCallback(
    (template) => {
      if (!template || !template.imageUrl) {
        return;
      }

      const title = toTitleCase(template.title || 'Published Template');
      createCollection({
        title,
        curator: formatCuratorLabel(''),
        description: template.prompt || title,
        tags: normalizeTagList(template.tags),
        visibility: 'public',
        ownerId: currentUserId,
        artworks: [
          {
            id: template.sourceHistoryId || template.id,
            url: template.imageUrl,
            title,
            prompt: template.prompt || title,
            tags: normalizeTagList(template.tags),
          },
        ],
      });

      setStoredCollections(getAllCollections());
      setSelectedTemplate((previous) =>
        previous && previous.templateId === template.templateId
          ? { ...previous, visibility: 'public' }
          : previous,
      );
      message.success('Image published to Public Templates.');
    },
    [currentUserId],
  );

  const handleUnpublishHistoryTemplate = useCallback((template) => {
    if (!template) {
      return;
    }

    const collectionId = publishedTemplateState.collectionIds[template.templateId];
    if (collectionId) {
      updateCollection(collectionId, { visibility: 'private' });
    }

    setStoredCollections(getAllCollections());
    setSelectedTemplate((previous) =>
      previous && previous.templateId === template.templateId
        ? { ...previous, visibility: 'private' }
        : previous,
    );
    message.success('Image kept private.');
  }, [publishedTemplateState]);

  const handleDownloadTemplate = useCallback(async (template) => {
    if (!template || !template.imageUrl || downloadingTemplateId) {
      return;
    }

    try {
      setDownloadingTemplateId(template.templateId || template.id || template.imageUrl);
      await triggerDownloadFromUrl(template.imageUrl, template.filename || 'generated-image.png');
    } catch (error) {
      message.error('Unable to download image. Please try again.');
    } finally {
      setDownloadingTemplateId(null);
    }
  }, [downloadingTemplateId]);

  const renderTemplateGrid = () => {
    if (isLoading) {
      return (
        <p className="catalog-carousel__empty">
          Loading Gallery...
        </p>
      );
    }

    if (loadError) {
      return (
        <p className="catalog-carousel__empty">
          {loadError}
        </p>
      );
    }

    if (visibleTemplates.length === 0) {
      return (
        <p className="catalog-carousel__empty">
          {activeGalleryKind === 'character_swap'
            ? 'No Character Swap results in your Gallery yet.'
            : 'No Image Gen results in your Gallery yet. Generate an image to add one.'}
        </p>
      );
    }

    return (
      <>
        <div className="catalog-gallery__grid" aria-live="polite">
          {galleryColumns.map((column, columnIndex) => (
            <div className="catalog-gallery__column" key={`gallery-column-${columnIndex + 1}`}>
              {column.map(({ template, sourceIndex }) => {
                const isCharacterSwap = getTemplateKind(template) === 'character_swap';
                const isPublicTemplate = template.visibility === 'public';
                const extraMenuActions = isCharacterSwap
                  ? []
                  : [
                    isPublicTemplate
                      ? {
                          label: 'Keep Private',
                          iconClassName: 'fa-solid fa-lock',
                          onClick: () => handleUnpublishHistoryTemplate(template),
                        }
                      : {
                        label: 'Make Public',
                        iconClassName: 'fa-solid fa-globe',
                        onClick: () => handlePublishHistoryTemplate(template),
                      },
                  ];

                return (
                  <TemplateImageTile
                    key={template.templateId || template.id}
                    className={`catalog-template-tile catalog-template-tile--${(sourceIndex % 6) + 1}`}
                    imageUrl={template.imageUrl || template.url || fallbackImage}
                    fallbackImageUrl={fallbackImage}
                    title={template.title}
                    badgeLabel={isPublicTemplate ? 'Public' : ''}
                    badgeIconClassName={isPublicTemplate ? 'fa-solid fa-globe' : ''}
                    metaLabel={isCharacterSwap ? 'Character Swap' : undefined}
                    extraMenuActions={extraMenuActions}
                    interactive
                    showHoverActions={!isCharacterSwap}
                    onDetails={() => handleOpenTemplateDetails(template)}
                    onOpen={isCharacterSwap ? undefined : () => handleUseTemplate(template)}
                    onReprompt={isCharacterSwap ? undefined : () => handleUseTemplate(template)}
                    onStyleReference={isCharacterSwap ? undefined : () => handleUseTemplate(template, 'style')}
                  />
                );
              })}
              {isLoadingMoreTemplates && columnIndex < Math.min(galleryColumnCount, 5) && (
                <div
                  className={`catalog-gallery__skeleton catalog-gallery__skeleton--${(columnIndex % 3) + 1}`}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
        {hasMoreVisibleTemplates && (
          <div ref={loadMoreRef} className="catalog-gallery__load-more" aria-hidden="true">
            {!isLoadingMoreTemplates && <span />}
          </div>
        )}
      </>
    );
  };

  return (
    <section className="catalog gallery-page">
      <section className="catalog__section catalog-gallery" aria-labelledby="gallery-title">
        <div className="catalog__section-header catalog__section-header--templates">
          <div className="catalog__section-title-row">
            <div>
              <span className="catalog-hero__eyebrow">Generated images</span>
              <h1 id="gallery-title" className="gallery-page__title">Gallery</h1>
            </div>
            <div className="catalog-gallery__tabs" role="tablist" aria-label="Gallery type">
              <button
                type="button"
                className={`catalog-gallery__tab${activeGalleryKind === 'image_gen' ? ' is-active' : ''}`}
                role="tab"
                aria-selected={activeGalleryKind === 'image_gen'}
                onClick={() => setActiveGalleryKind('image_gen')}
              >
                Image Gen
                <span>{galleryCounts.image_gen}</span>
              </button>
              <button
                type="button"
                className={`catalog-gallery__tab${activeGalleryKind === 'character_swap' ? ' is-active' : ''}`}
                role="tab"
                aria-selected={activeGalleryKind === 'character_swap'}
                onClick={() => setActiveGalleryKind('character_swap')}
              >
                Character Swap
                <span>{galleryCounts.character_swap}</span>
              </button>
            </div>
          </div>
          <p>
            Your generated images are available here as reusable private templates.
          </p>
        </div>

        {renderTemplateGrid()}
      </section>

      {selectedTemplate && (
        <div
          className="catalog-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-detail-title"
        >
          <div
            className="catalog-modal__backdrop"
            role="presentation"
            onClick={handleCloseTemplateDetails}
          />
          <div className="catalog-modal__panel catalog-modal__panel--template-detail">
            <button
              type="button"
              className="catalog-modal__close"
              onClick={handleCloseTemplateDetails}
              aria-label="Close image details"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <div className="catalog-template-detail">
              <figure className="catalog-template-detail__media">
                <img
                  src={selectedTemplate.imageUrl || selectedTemplate.url || fallbackImage}
                  alt={selectedTemplate.title || 'Generated image'}
                  onError={(event) => {
                    event.currentTarget.src = fallbackImage;
                  }}
                />
              </figure>
              <div className="catalog-template-detail__body">
                <header className="catalog-template-detail__header">
                  <span className="catalog-template-detail__eyebrow">
                    {getTemplateKind(selectedTemplate) === 'character_swap'
                      ? 'Character Swap'
                      : 'Image Gen'}
                  </span>
                  <h2 id="template-detail-title">
                    {selectedTemplate.title || 'Generated image'}
                  </h2>
                </header>

                <div className="catalog-template-detail__field">
                  <span>Generated</span>
                  <p>{formatGeneratedTime(selectedTemplate.createdAt)}</p>
                </div>

                {getTemplateKind(selectedTemplate) === 'character_swap' ? (
                  <p className="catalog-template-detail__note">
                    Character Swap results are shown with minimal metadata and can be reused as
                    visual references.
                  </p>
                ) : (
                  <div className="catalog-template-detail__field">
                    <span>Prompt</span>
                    <p className="catalog-template-detail__prompt-text">
                      {selectedTemplate.prompt || 'No prompt saved for this image.'}
                    </p>
                  </div>
                )}

                <div className="catalog-template-detail__field">
                  <span>Tags</span>
                  {Array.isArray(selectedTemplate.tags) && selectedTemplate.tags.length > 0 ? (
                    <ul className="catalog-template-detail__tags">
                      {selectedTemplate.tags.map((tag) => (
                        <li key={`${selectedTemplate.templateId || selectedTemplate.id}-${tag}`}>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No tags saved yet.</p>
                  )}
                </div>

                <div className="catalog-template-detail__actions">
                  <button
                    type="button"
                    onClick={() => handleDownloadTemplate(selectedTemplate)}
                    disabled={Boolean(downloadingTemplateId)}
                  >
                    <i className="fa-solid fa-download" aria-hidden="true" />
                    Download
                  </button>
                  {getTemplateKind(selectedTemplate) !== 'character_swap' && (
                    <>
                      <button type="button" onClick={() => handleUseTemplate(selectedTemplate)}>
                        Reprompt
                      </button>
                      <button type="button" onClick={() => handleUseTemplate(selectedTemplate, 'style')}>
                        Style Reference
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
